/**
 * TIA AI-core avatar — the animated centrepiece of the assistant panel.
 * Ported precisely from the design handoff prototype (Ticksy.dc.html):
 * a glossy dark-purple rounded-square core with antenna, glass visor and
 * glowing LED eyes, inside an ambient glow. All styling/motion lives in
 * tia-avatar.css; per-state bindings key off `tk-state-<state>`.
 */
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

import { AGENT_STATE_META } from '../../types/agent-state.types';
import type { AgentState } from '../../types/agent-state.types';
import './tia-avatar.css';

export type TiaReaction = 'ack' | 'blink';

export interface TiaAvatarProps {
  /** Agent lifecycle state — drives glow, motion, face and label. */
  state: AgentState;
  /** Avatar body size in px (the label sits below it). */
  size?: number;
  /**
   * Render the status label under the body (default). The companion header
   * turns this off and shows its own narration-aware label beside the name.
   */
  showLabel?: boolean;
  /** Optional one-shot micro-reaction overlaid on the current state. */
  reaction?: TiaReaction | null;
  /** Called once, ~700ms after entering 'done' (the pop played). */
  onSettled?: () => void;
  /** Called once after a reaction cycle completes. */
  onReactionEnd?: () => void;
}

const DONE_SETTLE_MS = 700;
const REACTION_MS: Record<TiaReaction, number> = { ack: 300, blink: 600 };
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(REDUCED_MOTION_QUERY);
    setReduced(mql.matches);
    const onChange = (ev: MediaQueryListEvent) => setReduced(ev.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function Spark() {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='currentColor'
      className='tk-spark-svg'
    >
      <path d='M12 0l2.4 7.6L22 10l-7.6 2.4L12 20l-2.4-7.6L2 10l7.6-2.4z' />
    </svg>
  );
}

export function TiaAvatar({
  state,
  size = 110,
  showLabel = true,
  reaction = null,
  onSettled,
  onReactionEnd,
}: TiaAvatarProps) {
  const meta = AGENT_STATE_META[state];
  const reduced = usePrefersReducedMotion();

  // Latest-callback refs so timer effects don't re-arm on identity changes.
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const onReactionEndRef = useRef(onReactionEnd);
  onReactionEndRef.current = onReactionEnd;

  // 'done' plays its pop once (~0.7s), then the owner settles it to idle.
  useEffect(() => {
    if (state !== 'done') return undefined;
    const timer = window.setTimeout(
      () => onSettledRef.current?.(),
      DONE_SETTLE_MS
    );
    return () => window.clearTimeout(timer);
  }, [state]);

  // A micro-reaction lasts one cycle, then reports back.
  useEffect(() => {
    if (!reaction) return undefined;
    const timer = window.setTimeout(
      () => onReactionEndRef.current?.(),
      REACTION_MS[reaction]
    );
    return () => window.clearTimeout(timer);
  }, [reaction]);

  const showNormalEyes =
    state === 'idle' ||
    state === 'thinking' ||
    state === 'searching' ||
    state === 'confirm';

  const rootClass = [
    'tk-avatar',
    `tk-state-${state}`,
    reduced ? 'tk-reduced' : '',
    reaction ? `tk-react-${reaction}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      data-slot='tia-avatar'
      className={rootClass}
      style={{ width: size, '--tk-glow': meta.glow } as CSSProperties}
    >
      <div
        className='tk-stage'
        style={{ width: size, height: size }}
        aria-hidden='true'
      >
        <div className='tk-glow-amb' />

        {state === 'confirm' && (
          <>
            <div className='tk-ring' />
            <div className='tk-ring tk-ring-2' />
          </>
        )}

        {state === 'thinking' && (
          <div className='tk-orbit'>
            <div className='tk-orbit-node' />
          </div>
        )}

        <div className='tk-core'>
          <div className='tk-antenna' />
          <div className='tk-antenna-tip' />

          <div className='tk-shell'>
            <div className='tk-sheen' />
            <div className='tk-dot tk-dot-l' />
            <div className='tk-dot tk-dot-r' />

            <div className='tk-visor'>
              {showNormalEyes && (
                <>
                  <div className='tk-eye' />
                  <div className='tk-eye' />
                </>
              )}
              {state === 'done' && (
                <>
                  <div className='tk-eye-happy' />
                  <div className='tk-eye-happy' />
                </>
              )}
              {state === 'error' && (
                <>
                  <div className='tk-eye-dash' />
                  <div className='tk-eye-dash' />
                </>
              )}
              {state === 'writing' && (
                <div className='tk-eq'>
                  <div className='tk-eq-bar tk-eq-bar-1' />
                  <div className='tk-eq-bar tk-eq-bar-2' />
                  <div className='tk-eq-bar tk-eq-bar-3' />
                </div>
              )}
              {state === 'searching' && <div className='tk-scan' />}
            </div>

            {state === 'writing' && <div className='tk-voice' />}
          </div>

          {state === 'done' && (
            <>
              <div className='tk-spark tk-spark-1'>
                <Spark />
              </div>
              <div className='tk-spark tk-spark-2'>
                <Spark />
              </div>
            </>
          )}
        </div>
      </div>

      {showLabel && (
        <div
          className='tk-status'
          role='status'
          style={{ color: meta.labelColor }}
        >
          {meta.label}
        </div>
      )}
    </div>
  );
}

/** Static 28px mini avatar shown beside assistant chat messages. */
export function TiaMini({ size = 28 }: { size?: number }) {
  return (
    <div
      data-slot='tia-mini'
      className='tk-mini'
      style={{ width: size, height: size }}
      aria-hidden='true'
    >
      <div className='tk-mini-visor'>
        <span className='tk-mini-eye' />
        <span className='tk-mini-eye' />
      </div>
    </div>
  );
}
